import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { GetPoliciesData, getClaimsData } from "../../Dashboard/DashboardAction";
import { getCookie } from "../../../utils/Utility";
import CustomIcon from "../../../common/CustomIcon/CustomIcon";

interface MentionItem {
  key: string;
  index: number;
  id: string;
  subtitle: string;
  icon?: React.ReactNode;
  section: string;
}

interface MentionSection {
  title: string;
  items: MentionItem[];
}

type MentionVariant = "insureAssist" | "chatHeader";

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  onSendMessage?: (message: string) => void;
  variant?: MentionVariant;
}

const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder,
  disabled = false,
  inputRef,
  onSendMessage,
  variant = "insureAssist",
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { policiesData, claimsData } = useAppSelector((s: RootState) => s.dashboard);
  
  const [showMentionPopup, setShowMentionPopup] = useState<boolean>(false);
  const [mentionQuery, setMentionQuery] = useState<string>("");
  const [mentionActiveIndex, setMentionActiveIndex] = useState<number>(0);
  const mentionContainerRef = useRef<HTMLDivElement | null>(null);

  const getIcon = (linecode: string) => {
    switch (linecode) {
      case "PA":
        return (
          <Box sx={{ width: "25px", height: "25px", "& svg": { width: "100%", height: "100%" } }}>
            <CustomIcon iconName="Car" />
          </Box>
        );
      case "HO":
        return (
          <Box sx={{ width: "25px", height: "25px", "& svg": { width: "100%", height: "100%" } }}>
            <CustomIcon iconName="Home" />
          </Box>
        );
      case "PU":
        return (
          <Box sx={{ width: "25px", height: "25px", "& svg": { width: "100%", height: "100%" } }}>
            <CustomIcon iconName="Umbrella" />
          </Box>
        );
      case "BOP":
        return (
          <Box sx={{ width: "25px", height: "25px", "& svg": { width: "100%", height: "100%" } }}>
            <CustomIcon iconName="Business" />
          </Box>
        );
      case "FO":
        return (
          <Box sx={{ width: "25px", height: "25px", "& svg": { width: "100%", height: "100%" } }}>
            <CustomIcon iconName="Farm" />
          </Box>
        );
      default:
        return null;
    }
  };

  const mentionSections = useMemo<MentionSection[]>(() => {
    const openClaims = (claimsData?.claimList || []).filter((c: any) => c.carrierClaimStatus !== "Closed");
    const closedClaims = (claimsData?.claimList || []).filter((c: any) => c.carrierClaimStatus === "Closed");
    const allPolicies = (policiesData?.policyList || []);
    const cancelledPolicies = allPolicies.filter((p: any) => (p.carrierPolicyStatus || "").toLowerCase() === "cancelled");
    const openPolicies = allPolicies.filter((p: any) => (p.carrierPolicyStatus || "").toLowerCase() !== "cancelled");

    return [
      {
        title: t("Open Policies"),
        items: openPolicies.map((p: any) => ({
          key: `op-${p.policyId}`,
          index: 0,
          id: p.carrierPolicyNo,
          subtitle: `${p.insuredName} • ${p.carrierPolicyStatus}`,
          icon: getIcon(p.lobCode),
          section: t("Open Policies"),
        })),
      },
      {
        title: t("Open Claims"),
        items: openClaims.map((c: any, idx: number) => ({
          key: `oc-${c.claimId}`,
          index: 0,
          id: c.carrierClaimNo,
          subtitle: `${t("Policy")} ${c.carrierPolicyNo} • ${c.carrierClaimStatus}`,
          icon: getIcon(c.lobCode),
          section: t("Open Claims"),
        })),
      },
      {
        title: t("Closed Claims"),
        items: closedClaims.map((c: any) => ({
          key: `cc-${c.claimId}`,
          index: 0,
          id: c.carrierClaimNo,
          subtitle: `${t("Policy")} ${c.carrierPolicyNo} • ${c.carrierClaimStatus}`,
          icon: getIcon(c.lobCode),
          section: t("Closed Claims"),
        })),
      },
      {
        title: t("Cancelled Policies"),
        items: cancelledPolicies.map((p: any) => ({
          key: `cp-${p.policyId}`,
          index: 0,
          id: p.carrierPolicyNo,
          subtitle: `${p.insuredName} • ${p.carrierPolicyStatus}`,
          icon: getIcon(p.lobCode),
          section: t("Cancelled Policies"),
        })),
      },
    ];
  }, [claimsData?.claimList, policiesData?.policyList, t]);

  // Load policies/claims data when component mounts
  useEffect(() => {
    const customerId = getCookie("id");
    const carrier = getCookie("carrier");
    const system = getCookie("system");

    if (customerId === "0987") {
      return;
    }

    if (customerId && carrier && system) {
      if (!policiesData?.policyList) {
        dispatch(
          GetPoliciesData({
            customerId,
            carrier,
            system,
          }) as any
        );
      }
      if (!claimsData?.claimList) {
        dispatch(
          getClaimsData({
            customerId,
            carrier,
            system,
          }) as any
        );
      }
    }
  }, [dispatch]);

  const flatMentionItems = useMemo<MentionItem[]>(() => {
    const q = mentionQuery.trim().toLowerCase();
    let counter = -1;
    const arr: MentionItem[] = mentionSections.flatMap((section) =>
      section.items.map((it) => {
        counter += 1;
        return { ...it, index: counter } as MentionItem;
      })
    );
    if (!q) return arr;
    return arr.filter(
      (it) =>
        it.id.toLowerCase().includes(q) ||
        it.subtitle.toLowerCase().includes(q)
    );
  }, [mentionSections, mentionQuery]);

  const updateMentionState = useCallback(() => {
    const el = inputRef?.current;
    if (!el) return;
    const value = el.value;
    const caret = el.selectionStart ?? value.length;
    const uptoCaret = value.slice(0, caret);
    const atIndex = uptoCaret.lastIndexOf("@");
    const hasSpaceAfterAt = atIndex >= 0 && /\s/.test(uptoCaret.slice(atIndex + 1));
    
    if (atIndex >= 0 && !hasSpaceAfterAt) {
      const query = uptoCaret.slice(atIndex + 1);
      setMentionQuery(query);
      setShowMentionPopup(true);
      setMentionActiveIndex(0);
    } else {
      setShowMentionPopup(false);
      setMentionQuery("");
    }
  }, [inputRef]);

  const handleMentionPick = useCallback((item: MentionItem) => {
    const el = inputRef?.current;
    if (!el) return;
    const caret = el.selectionStart ?? el.value.length;
    const uptoCaret = el.value.slice(0, caret);
    const atIndex = uptoCaret.lastIndexOf("@");
    
    if (atIndex >= 0) {
      const newValue =
        el.value.slice(0, atIndex) + `${item.id} ` + el.value.slice(caret);
      onChange(newValue);
      setTimeout(() => {
        try {
          el.focus();
          const pos = (el.value.slice(0, atIndex) + `${item.id} `).length;
          el.setSelectionRange(pos, pos);
        } catch (e) {
          // Handle focus errors silently
        }
      }, 0);
    }
    setShowMentionPopup(false);
  }, [inputRef, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showMentionPopup || flatMentionItems.length === 0) {
      onKeyDown?.(e);
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setMentionActiveIndex((i) => (i + 1) % flatMentionItems.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setMentionActiveIndex((i) => (i - 1 + flatMentionItems.length) % flatMentionItems.length);
        break;
      case "Enter":
        if (flatMentionItems[mentionActiveIndex]) {
          e.preventDefault();
          handleMentionPick(flatMentionItems[mentionActiveIndex]);
        } else {
          onKeyDown?.(e);
        }
        break;
      case "Escape":
        setShowMentionPopup(false);
        break;
      default:
        onKeyDown?.(e);
    }
  }, [showMentionPopup, flatMentionItems, mentionActiveIndex, handleMentionPick, onKeyDown]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    updateMentionState();
  }, [onChange, updateMentionState]);

  const isChatHeader = variant === "chatHeader";

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <input
        autoFocus={true}
        type="text"
        placeholder={placeholder}
        style={{
          fontSize: "16px",
          padding: "10px",
          border: "none",
          borderRadius: isChatHeader ? "8px" : "50px",
          width: "100%",
          outline: "none",
          fontFamily: `ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI Variable Display", "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"`,
        }}
        value={value}
        onChange={handleInputChange}
        disabled={disabled}
        ref={inputRef}
        onKeyDown={handleKeyDown}
      />
      
      {showMentionPopup && (
        <Box
          sx={{
            position: "absolute",
            bottom: isChatHeader?"auto":"56px",
            top: isChatHeader?"3rem":"auto",
            left: isChatHeader ? 0 : 16,
            width: isChatHeader
              ? { xs: "80%", sm: "40%", md: "60%", lg: "40%" }
              : "100%",
            maxHeight: 280,
            overflowY: "auto",
            zIndex: 10,
          }}
        >
          <Box component={"div"} sx={{ borderRadius: 2, boxShadow: 3, bgcolor: "background.paper" }}>
            <Box sx={{ p: 1 }}>
              {mentionSections
                .map((s) => ({
                  ...s,
                  items: flatMentionItems.filter((it) => it.section === s.title),
                }))
                .map((section, sIdx) => {
                  if (!section.items || section.items.length === 0) return null;
                  return (
                    <Box key={sIdx} sx={{ mb: 1, border: "1px solid #eee", borderRadius: 1 }}>
                      <Box sx={{ px: 1, py: 0.5, fontSize: 12, color: "#666" }}>
                        {section.title}
                      </Box>
                      <Box>
                        {section.items.map((item) => {
                          const active = item.index === mentionActiveIndex;
                          return (
                            <Box
                              key={item.key}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleMentionPick(item);
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1,
                                py: 0.75,
                                cursor: "pointer",
                                bgcolor: active ? "rgba(0,0,0,0.06)" : undefined,
                                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                              }}
                            >
                              {item.icon}
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Box sx={{ fontSize: 13 }}>{item.id}</Box>
                                <Box sx={{ fontSize: 11, color: "#666" }}>{item.subtitle}</Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MentionInput;
